/**
 * Roadside dry-stone walls (issue #22): the drystone field walls that line most
 * Northumberland roads, extruded along the verges of the OSM tarmac-road network
 * (data/config/roads.json). Mirrors BuildingsLayer / WaterLayer — pure
 * three.js-free helpers unit-tested in Node, wrapped in a three.js Group with
 * update(sampleGround) so walls drape as terrain tiles stream in.
 *
 * Purely landscape furniture: walls have NO bearing on any LOS result (hard
 * constraint #1, guarded by roadWalls.guard.test.ts) and no elevation is
 * invented — every wall vertex sits on the DTM via the same collider
 * sampleGround that backs walk-mode ground lock (hard constraint #6). Positions
 * are illustrative (walls assumed along tarmac roads; not a surveyed inventory).
 *
 * Variety comes from a deterministic per-segment hash (the hash01 idiom from
 * buildings.ts / turbines.ts), so it is reproducible, not random per frame:
 * height jitter, a grey/sandstone colour jitter (vertex colours), and occasional
 * gaps (field gateways). Geometry is MERGED into one mesh to keep draw calls low
 * (issue #17).
 *
 * Perf contract (fix for the 2 s animate/freeze cycle): a verge vertex is
 * raycast only while unresolved — a resolved height is cached forever and
 * never re-asked; update() takes a per-call sample budget and reports whether
 * work remains; walls build the resolved portions of a road instead of
 * waiting for every vertex (roads crossing the terrain edge used to re-sample
 * ~14k vertices every tick, forever); and roads outside the route corridor
 * are dropped at construction (walls are only visually discernible near the
 * route). Built geometry stays once built (persistent, like the beacons).
 */
import { BufferAttribute, BufferGeometry, Color, DoubleSide, Group, Mesh, MeshStandardMaterial } from 'three';
import { bngToLocal, type LocalXZ } from '../core/coordinates';
import type { Road, RoadsFile } from '../core/manifest';
import type { GroundSampler } from '../camera/groundLock';
import { clipToCorridor } from './corridor';
import { makeDrystoneTexture } from './drystoneTexture';

/** Only roads within this distance of the route corridor get walls; distant
 * drystone walls are half a metre wide and not visually discernible. */
export const WALL_CORRIDOR_M = 300;
/** Perpendicular offset from the road centre to each verge wall (metres):
 * ~half a single-track lane + a small margin. */
export const VERGE_OFFSET_M = 3;
export const WALL_THICKNESS_M = 0.5;
/** Top-of-wall thickness (batter): real walls taper toward the top. */
export const WALL_TOP_THICKNESS_M = 0.35;
export const WALL_MIN_H_M = 0.9;
export const WALL_MAX_H_M = 1.5;
/** Spacing the road centreline is resampled to before walling (metres). */
export const WALL_DENSIFY_M = 8;
/** Per-segment probability of a gap (a field gateway). */
const GAP_PROB = 0.06;
/** Road classes that get walls by default — the actual lanes, not service
 * roads (driveways / farm access / parking aisles). Trunk roads (issue #45:
 * A696/A68) are deliberately EXCLUDED too — dry-stone verge walls are a
 * minor-road feature; arterial trunk roads typically run open verges/fencing,
 * and draping walls the full length of the AOI's two busiest roads would be
 * both wrong-looking and a needless geometry cost (issue #17). Since issue #138
 * added 'trunk' to `Road['road_class']`, this exclusion is enforced only at
 * runtime (trunk is absent from this default set), no longer structurally by
 * the type — the roadWalls tests assert a trunk road gets no walls. */
export const DEFAULT_WALL_CLASSES: Road['road_class'][] = ['primary', 'secondary', 'tertiary', 'unclassified'];

/** Deterministic fractional hash in [0, 1) seeded by an integer (same idiom as
 * buildings.ts / turbines.ts). */
export function hash01(i: number): number {
  const x = Math.sin(i * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

export function roadLocalLine(polyline: [number, number][]): LocalXZ[] {
  return polyline.map(([e, n]) => bngToLocal(e, n));
}

const TWO_PI = Math.PI * 2;
/** Peak of the wall-top undulation above a stretch's base (m), i.e. the sum of
 * the two wave amplitudes below. The undulation spans [0, 2*this]; the base
 * range is shrunk by 2*this so base + undulation always stays within
 * [WALL_MIN_H_M, WALL_MAX_H_M]. */
const WALL_UNDULATION_AMP_M = 0.17;

/** Wall height (m) at verge vertex i of a road — the wall body's top line.
 *
 * Two parts: a per-stretch base so neighbouring walls differ visibly, and an
 * undulation of the top line ALONG the stretch (Simon: the masonry itself must
 * rise and dip, not read dead straight). The undulation is the sum of two
 * per-road sines:
 *   - a long wave (amp 0.10 m, 8-14 rings ≈ 65-140 m) — the smooth settling
 *     rise-and-dip; it survives low-pass smoothing, so it reads as real
 *     undulation rather than white noise;
 *   - a short wave (amp 0.07 m, 2-3.5 rings ≈ 16-35 m) — local unevenness that
 *     makes the ripple visible within a single on-screen span of wall.
 * At the 8 m ring spacing the short wave is near the sampling limit, so it is
 * deliberately a little ragged vertex-to-vertex (correct for stone, not a
 * smooth CAD curve). Both are hashed from roadSeed (deterministic; no
 * Math.random). The sum stays within [WALL_MIN_H_M, WALL_MAX_H_M]. */
export function wallHeightAt(roadSeed: number, i: number): number {
  const base = WALL_MIN_H_M + hash01(roadSeed * 7.7) * (WALL_MAX_H_M - WALL_MIN_H_M - 2 * WALL_UNDULATION_AMP_M);
  const longWavelen = 8 + hash01(roadSeed * 3.1) * 6; // 8-14 rings
  const longPhase = hash01(roadSeed * 5.9) * TWO_PI;
  const shortWavelen = 2 + hash01(roadSeed * 8.3) * 1.5; // 2-3.5 rings
  const shortPhase = hash01(roadSeed * 11.7) * TWO_PI;
  const undulation =
    Math.sin((i / longWavelen) * TWO_PI + longPhase) * 0.1 +
    Math.sin((i / shortWavelen) * TWO_PI + shortPhase) * 0.07;
  return base + WALL_UNDULATION_AMP_M + undulation;
}

/** Whether segment i of a road is left open as an occasional field gateway. */
function isGateway(roadSeed: number, i: number): boolean {
  return hash01(roadSeed * 977 + i * 7.3) < GAP_PROB;
}

/** Cumulative along-run distance (m) at each verge vertex, for the wall body's
 * metre-based UVs. Computed once per verge. */
function cumulativeDistances(verge: LocalXZ[]): number[] {
  const dist: number[] = new Array(verge.length).fill(0);
  for (let i = 1; i < verge.length; i++) {
    dist[i] = dist[i - 1] + Math.hypot(verge[i].x - verge[i - 1].x, verge[i].z - verge[i - 1].z);
  }
  return dist;
}

/** Resample a local polyline to ~spacingM between points (denser = smoother
 * drape + wall top), preserving the original endpoints. Exported for reuse by
 * other verge-offset scene furniture (vegetationPlacement.ts, issue #44) so
 * road-following layers share one densify/perpendiculars implementation. */
export function densify(line: LocalXZ[], spacingM: number): LocalXZ[] {
  if (line.length < 2) return line.slice();
  const out: LocalXZ[] = [line[0]];
  for (let i = 1; i < line.length; i++) {
    const a = line[i - 1];
    const b = line[i];
    const segLen = Math.hypot(b.x - a.x, b.z - a.z);
    const steps = Math.max(1, Math.floor(segLen / spacingM));
    for (let s = 1; s <= steps; s++) {
      const t = s / steps;
      out.push({ x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t });
    }
  }
  return out;
}

/** Per-vertex horizontal perpendiculars (unit) for a polyline. Exported for
 * reuse by vegetationPlacement.ts (issue #44) — see densify() above. */
export function perpendiculars(line: LocalXZ[]): Array<{ x: number; z: number }> {
  const n = line.length;
  const out: Array<{ x: number; z: number }> = [];
  for (let i = 0; i < n; i++) {
    const prev = line[Math.max(0, i - 1)];
    const next = line[Math.min(n - 1, i + 1)];
    let tx = next.x - prev.x;
    let tz = next.z - prev.z;
    const len = Math.hypot(tx, tz) || 1;
    tx /= len;
    tz /= len;
    out.push({ x: -tz, z: tx });
  }
  return out;
}

interface Accum {
  positions: number[];
  colors: number[];
  uvs: number[];
  indices: number[];
}

/** Append one verge wall (draped, with per-vertex height, thickness, gaps and a
 * per-road stone colour) into the shared arrays. Vertices whose ground has not
 * resolved yet are skipped: only segments between two consecutive resolved
 * vertices are built, so a road crossing the terrain edge still gets the walls
 * that CAN exist (nothing is fabricated over missing ground). Hashes are
 * seeded by the absolute vertex index, so geometry already built does not
 * change shape as more of the road resolves. */
function appendWall(
  verge: LocalXZ[],
  groundY: Array<number | null>,
  dist: number[],
  roadSeed: number,
  color: Color,
  acc: Accum,
): void {
  const n = verge.length;
  if (n < 2) return;
  const perp = perpendiculars(verge);
  const t = WALL_THICKNESS_M / 2;
  const tTop = WALL_TOP_THICKNESS_M / 2;

  // For each resolved vertex, four corners (inner/outer × base/top). Height
  // varies per vertex so the top course is uneven like real drystone. The top
  // ring is pulled in (batter), so only the base pair sits at full thickness.
  const ringStart: number[] = new Array(n).fill(-1);
  for (let i = 0; i < n; i++) {
    const g = groundY[i];
    if (g === null) continue;
    const h = wallHeightAt(roadSeed, i);
    ringStart[i] = acc.positions.length / 3;
    // 0: inner base, 1: inner top, 2: outer base, 3: outer top
    acc.positions.push(
      verge[i].x + perp[i].x * t, g, verge[i].z + perp[i].z * t,
      verge[i].x + perp[i].x * tTop, g + h, verge[i].z + perp[i].z * tTop,
      verge[i].x - perp[i].x * t, g, verge[i].z - perp[i].z * t,
      verge[i].x - perp[i].x * tTop, g + h, verge[i].z - perp[i].z * tTop,
    );
    // UVs in metres: u along the run, v from base (0) to wall height
    acc.uvs.push(dist[i], 0, dist[i], h, dist[i], 0, dist[i], h);
    for (let k = 0; k < 4; k++) acc.colors.push(color.r, color.g, color.b);
  }

  for (let i = 0; i < n - 1; i++) {
    if (ringStart[i] < 0 || ringStart[i + 1] < 0) continue; // unresolved end
    if (isGateway(roadSeed, i)) continue; // occasional field gateway
    const a = ringStart[i];
    const b = ringStart[i + 1];
    const iBaseA = a + 0;
    const iTopA = a + 1;
    const oBaseA = a + 2;
    const oTopA = a + 3;
    const iBaseB = b + 0;
    const iTopB = b + 1;
    const oBaseB = b + 2;
    const oTopB = b + 3;
    // inner face, outer face, top face (two triangles each)
    acc.indices.push(iBaseA, iTopA, iBaseB, iBaseB, iTopA, iTopB);
    acc.indices.push(oBaseA, oBaseB, oTopA, oTopA, oBaseB, oTopB);
    acc.indices.push(iTopA, oTopA, iTopB, iTopB, oTopA, oTopB);
  }
}

interface RoadState {
  seed: number;
  color: Color;
  left: LocalXZ[];
  right: LocalXZ[];
  /** Cached ground height per verge vertex; null = not resolved yet. A
   * resolved height is never re-sampled (the perf contract). */
  leftGround: Array<number | null>;
  rightGround: Array<number | null>;
  /** Tile generation at which each still-null vertex was last attempted —
   * a vertex is asked at most once per tile set (NaN = never attempted). */
  leftAttempt: number[];
  rightAttempt: number[];
  /** Count of still-null entries across both verges. */
  pending: number;
}

/** Per-road tint multiplied over the drystone texture: near-white with a
 * small warm/grey jitter so roads differ subtly without darkening the map. */
function stoneColor(seed: number): Color {
  const base = 0.85 + hash01(seed * 2.7) * 0.1;
  const warm = hash01(seed * 5.1) * 0.04;
  return new Color(base + warm, base + warm * 0.5, base);
}

export class RoadWallsLayer {
  readonly group = new Group();
  private readonly material: MeshStandardMaterial;
  private readonly roads: RoadState[] = [];
  private mesh?: Mesh;
  /** Synthetic generations (negative, so they never collide with real tile
   * generations) handed out when update() is called without one. */
  private fallbackGeneration = 0;

  constructor(
    roadsFile: RoadsFile,
    options: {
      wallClasses?: Road['road_class'][];
      /** Route polyline (local coords); roads beyond corridorMaxDistM of it
       * are dropped entirely — never sampled, never built. */
      corridor?: LocalXZ[];
      corridorMaxDistM?: number;
    } = {},
  ) {
    this.group.name = 'road-walls';
    const wallClasses = new Set(options.wallClasses ?? DEFAULT_WALL_CLASSES);
    const corridorMaxDistM = options.corridorMaxDistM ?? WALL_CORRIDOR_M;
    this.material = new MeshStandardMaterial({
      map: makeDrystoneTexture(1),
      vertexColors: true,
      roughness: 0.95,
      metalness: 0,
      side: DoubleSide,
    });

    let idx = 0;
    for (const road of roadsFile.roads) {
      if (!wallClasses.has(road.road_class)) continue;
      const centre = densify(roadLocalLine(road.polyline_bng), WALL_DENSIFY_M);
      if (centre.length < 2) continue;
      const runs = options.corridor ? clipToCorridor(centre, options.corridor, corridorMaxDistM) : [centre];
      for (const run of runs) {
        const perp = perpendiculars(run);
        const left = run.map((p, i) => ({ x: p.x + perp[i].x * VERGE_OFFSET_M, z: p.z + perp[i].z * VERGE_OFFSET_M }));
        const right = run.map((p, i) => ({ x: p.x - perp[i].x * VERGE_OFFSET_M, z: p.z - perp[i].z * VERGE_OFFSET_M }));
        const seed = idx + 1;
        this.roads.push({
          seed,
          color: stoneColor(seed),
          left,
          right,
          leftGround: new Array<number | null>(run.length).fill(null),
          rightGround: new Array<number | null>(run.length).fill(null),
          leftAttempt: new Array<number>(run.length).fill(Number.NaN),
          rightAttempt: new Array<number>(run.length).fill(Number.NaN),
          pending: run.length * 2,
        });
        idx += 1;
      }
    }
  }

  /**
   * Resolves ground heights for verge vertices that don't have one yet, up to
   * maxSamples raycasts this call, and rebuilds the merged mesh if anything
   * newly resolved. Resolved heights are cached forever (a tile unloading
   * never un-builds a wall); a vertex that stays null is only re-tried on a
   * later call (the caller gates those on the tile set actually changing).
   *
   * Returns true if the sample budget ran out before every pending vertex was
   * attempted — i.e. "call me again, there may be more ground to find now".
   *
   * `generation` identifies the current terrain tile set (see
   * TerrainCollider.generation): a still-null vertex is attempted at most once
   * per generation, so a road hanging off the map edge costs nothing once the
   * whole layer has been scanned for the current tiles. Omitting it retries
   * every still-null vertex (each call behaves as a fresh generation).
   */
  update(sampleGround: GroundSampler, maxSamples = Infinity, generation?: number): boolean {
    const gen = generation ?? (this.fallbackGeneration -= 1);
    let samples = 0;
    let changed = false;
    let exhausted = false;
    outer: for (const r of this.roads) {
      if (r.pending === 0) continue;
      for (const [verge, ground, attempt] of [
        [r.left, r.leftGround, r.leftAttempt],
        [r.right, r.rightGround, r.rightAttempt],
      ] as const) {
        for (let i = 0; i < verge.length; i++) {
          if (ground[i] !== null || attempt[i] === gen) continue;
          if (samples >= maxSamples) {
            exhausted = true;
            break outer;
          }
          samples += 1;
          attempt[i] = gen;
          const y = sampleGround(verge[i].x, verge[i].z);
          if (y !== null) {
            ground[i] = y;
            r.pending -= 1;
            changed = true;
          }
        }
      }
    }
    if (changed) this.rebuild();
    return exhausted;
  }

  private rebuild(): void {
    const acc: Accum = { positions: [], colors: [], uvs: [], indices: [] };
    for (const r of this.roads) {
      if (r.pending === r.left.length * 2) continue; // nothing resolved yet
      const leftDist = cumulativeDistances(r.left);
      const rightDist = cumulativeDistances(r.right);
      appendWall(r.left, r.leftGround, leftDist, r.seed * 2, r.color, acc);
      appendWall(r.right, r.rightGround, rightDist, r.seed * 2 + 1, r.color, acc);
    }

    if (this.mesh) {
      this.group.remove(this.mesh);
      this.mesh.geometry.dispose();
      this.mesh = undefined;
    }
    if (acc.positions.length === 0) return;

    const geo = new BufferGeometry();
    geo.setAttribute('position', new BufferAttribute(new Float32Array(acc.positions), 3));
    geo.setAttribute('color', new BufferAttribute(new Float32Array(acc.colors), 3));
    geo.setAttribute('uv', new BufferAttribute(new Float32Array(acc.uvs), 2));
    geo.setIndex(acc.indices);
    geo.computeVertexNormals();
    this.mesh = new Mesh(geo, this.material);
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;
    this.group.add(this.mesh);
  }
}
